/**
 * EncounterList
 *
 * Main component for displaying encounters/appointments with search,
 * filters, pagination, and responsive table/card views.
 * Enterprise-grade design with unified toolbar and refined visual hierarchy.
 *
 * Supports both standalone (Appointments) and connected (Encounters) modes.
 */

import React from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { EncounterListProvider, useEncounterList } from './EncounterListContext';
import { EncounterListConfig, UnifiedEncounterItem } from './types';
import {
  EncounterSearchBar,
  EncounterFilters,
  EncounterFilterPills,
  EncounterTable,
  EncounterCardList,
  EncounterListPagination,
  EncounterLoadMore,
  EncounterEmptyState,
} from './components';

// ============================================================================
// Skeleton Loading Component
// ============================================================================

function EncounterListSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-gray-200 rounded-full" />
            <div className="space-y-2 flex-1">
              <div className="h-4 bg-gray-200 rounded w-36" />
              <div className="h-3 bg-gray-100 rounded w-24" />
            </div>
            <div className="h-6 bg-gray-200 rounded-full w-20" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="h-3 bg-gray-100 rounded w-28" />
            <div className="h-3 bg-gray-100 rounded w-20" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Inner Component (with context access)
// ============================================================================

function EncounterListInner() {
  const {
    items,
    loading,
    loadingMore,
    error,
    pagination,
    config,
    isMobile,
    refresh,
  } = useEncounterList();
  const changeProvider = config.onChangeProvider
    ? (item: UnifiedEncounterItem) => config.onChangeProvider!(item, refresh)
    : undefined;

  const showMatchStatus = config.mode === 'connected' && config.showMatchStatus !== false;
  const showExternalSystem = config.mode === 'connected' && config.showExternalSystem !== false;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            {config.title || (config.mode === 'connected' ? 'Encounters' : 'Appointments')}
          </h1>
          {config.subtitle && (
            <p className="mt-1 text-sm text-gray-500">{config.subtitle}</p>
          )}
        </div>

        {config.canCreate && config.onCreateClick && config.mode === 'standalone' && (
          <button
            type="button"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-sm hover:shadow-md transition-all w-full sm:w-auto justify-center"
            onClick={config.onCreateClick}
          >
            <PlusIcon className="h-5 w-5" />
            <span>New Appointment</span>
          </button>
        )}

        {config.mode === 'connected' && (
          <div className="text-sm text-gray-500 w-full sm:w-auto text-center sm:text-left bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
            Encounters are managed through your EHR system
          </div>
        )}
      </div>

      {/* Unified Toolbar: Search + Filters */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-clinical-soft">
        <div className="p-4">
          <EncounterSearchBar />
        </div>
        <div className="px-4 pb-4">
          <EncounterFilters />
          <EncounterFilterPills />
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {loading && items.length === 0 ? (
            <span className="text-gray-400">Loading...</span>
          ) : (
            <>
              <span className="font-semibold text-gray-700">{pagination.total}</span>{' '}
              {config.mode === 'standalone'
                ? (pagination.total === 1 ? 'appointment' : 'appointments')
                : (pagination.total === 1 ? 'encounter' : 'encounters')} found
            </>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Content */}
      {!error && (
        <>
          {/* Loading State (initial) - Mobile skeleton, Desktop uses table skeleton */}
          {loading && items.length === 0 && isMobile && (
            <EncounterListSkeleton />
          )}

          {/* Empty State */}
          {!loading && items.length === 0 && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-clinical-soft">
              <EncounterEmptyState />
            </div>
          )}

          {/* Results */}
          {items.length > 0 && (
            <>
              {/* Desktop: Table View + Pagination integrated */}
              {!isMobile && (
                <div className="bg-white rounded-xl shadow-clinical-soft overflow-hidden">
                  <EncounterTable />
                  {config.enablePagination !== false && <EncounterListPagination />}
                </div>
              )}

              {/* Mobile: Card View */}
              {isMobile && (
                <EncounterCardList
                  items={items}
                  showMatchStatus={showMatchStatus}
                  showExternalSystem={showExternalSystem}
                  onViewDetails={config.onViewDetails || config.onItemClick}
                  onStartRecording={config.onStartRecording}
                  onViewTranscript={config.onViewTranscript}
                  onChangeProvider={changeProvider}
                  showProvider={config.mode === 'standalone'}
                />
              )}

              {/* Mobile: Infinite Scroll / Load More */}
              {isMobile && config.enableInfiniteScroll !== false && (
                <EncounterLoadMore />
              )}
            </>
          )}

          {/* Loading overlay for pagination */}
          {loading && items.length > 0 && !loadingMore && (
            <div className="fixed inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center z-10">
              <div className="bg-white rounded-xl shadow-clinical-medium p-4 flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                <span className="text-sm text-gray-600">Updating...</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================================
// Main Component (with provider)
// ============================================================================

interface EncounterListProps {
  config: EncounterListConfig;
}

export function EncounterList({ config }: EncounterListProps) {
  return (
    <EncounterListProvider config={config}>
      <EncounterListInner />
    </EncounterListProvider>
  );
}

export default EncounterList;
